import type { BlockDefinition, SocialItem, SocialPlatform, SocialRowProps } from '../types';
import { ensureHttps, maybeTokenize, safeUrl } from '../html';

/**
 * Inline SVG icons — no external requests, cache-friendly, theme-able via
 * `currentColor`. Kept compact (~200 chars each) so the rendered page stays
 * under 10KB for a typical bio profile.
 */
const ICONS: Record<SocialPlatform, string> = {
  instagram:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>',
  tiktok:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.89a4.85 4.85 0 0 1-1.84-.2z"/></svg>',
  x: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  youtube:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>',
  linkedin:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
  spotify:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>',
  threads:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.39 11.06c-.1-.05-.21-.09-.31-.14-.18-3.32-2-5.23-5.06-5.25h-.04c-1.83 0-3.36.78-4.3 2.21l1.69 1.16c.7-1.06 1.79-1.29 2.61-1.29h.03c1.02.01 1.79.31 2.29.89.36.42.6.99.72 1.72-.92-.16-1.92-.21-2.99-.15-3.01.17-4.95 1.93-4.82 4.37.07 1.24.69 2.31 1.74 3 .89.59 2.04.88 3.24.82 1.58-.09 2.82-.69 3.69-1.78.66-.83 1.08-1.9 1.27-3.25.78.47 1.36 1.09 1.68 1.83.54 1.27.57 3.34-1.12 5.03-1.48 1.48-3.26 2.12-5.95 2.14-2.98-.02-5.24-.98-6.71-2.84C2.65 17.81 2 14.78 2 12.01s.65-5.8 2.06-7.58c1.47-1.86 3.72-2.82 6.71-2.84 3.01.02 5.3.99 6.81 2.87.74.92 1.3 2.08 1.66 3.44l1.97-.53c-.45-1.68-1.16-3.12-2.12-4.31C17.16 1.05 14.39-.06 10.78 0 7.18.06 4.4 1.16 2.51 3.59 0 6.94 0 11.94 0 12.01s0 5.07 2.51 8.42C4.4 22.84 7.18 23.94 10.77 24h.05c3.19.02 5.43-.74 7.28-2.59 2.42-2.42 2.35-5.45 1.55-7.32-.57-1.34-1.66-2.42-3.16-3.13zm-5.21 4.93c-1.32.07-2.69-.52-2.76-1.79-.05-.94.67-1.99 2.85-2.12.25-.01.49-.02.73-.02.79 0 1.53.08 2.2.22-.25 3.13-1.73 3.65-3.02 3.72z"/></svg>',
  discord:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>',
  facebook:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
  website:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
  email:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>',
};

const ALL_PLATFORMS: SocialPlatform[] = [
  'instagram',
  'tiktok',
  'x',
  'youtube',
  'linkedin',
  'spotify',
  'threads',
  'discord',
  'facebook',
  'website',
  'email',
];

/** Cap per-icon URL pool; mirrors the standalone-rotator cap when this
 *  block also drives weighted rotation behind a single icon. */
const MAX_URLS_PER_ITEM = 100;

/**
 * Backwards-compat: items used to be `{ platform, url: string }`. New
 * items are `{ platform, urls: string[] }`. validateItem accepts either
 * shape and always returns the new shape so the rest of the codebase
 * doesn't have to branch.
 */
function validateItem(input: unknown): SocialItem | null {
  if (!input || typeof input !== 'object') return null;
  const i = input as Partial<SocialItem> & { url?: unknown };
  if (!ALL_PLATFORMS.includes(i.platform as SocialPlatform)) return null;
  const platform = i.platform as SocialPlatform;

  // Pull URLs out of EITHER `urls` (new) or `url` (legacy). Filter out
  // empties + truncate to MAX_URLS_PER_ITEM.
  let raw: unknown[] = [];
  if (Array.isArray(i.urls)) {
    raw = i.urls;
  } else if (typeof i.url === 'string') {
    raw = [i.url];
  }
  const urls = raw
    .filter((u): u is string => typeof u === 'string')
    .map((u) => u.trim())
    .filter((u) => u.length > 0)
    .map((u) => u.slice(0, 2000))
    .slice(0, MAX_URLS_PER_ITEM);
  if (urls.length === 0) return null;
  return { platform, urls };
}

export const socialRow: BlockDefinition<SocialRowProps> = {
  type: 'social-row',
  meta: {
    label: 'Social row',
    icon: '🔗',
    category: 'social',
    description:
      'Row of social profile icons. Give an icon multiple URLs and Poof rotates them per visitor — under-clicked URLs get pushed more so all your accounts grow evenly.',
  },
  defaults: () => ({
    items: [{ platform: 'instagram', urls: ['https://instagram.com/'] }],
    align: 'center',
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<SocialRowProps>;
    const rawItems = Array.isArray(i.items) ? i.items : [];
    return {
      items: rawItems
        .map(validateItem)
        .filter((x): x is SocialItem => x !== null)
        .slice(0, 12),
      align: ['left', 'center', 'right'].includes(i.align as string)
        ? (i.align as SocialRowProps['align'])
        : 'center',
    };
  },
  render(props, _theme, ctx) {
    if (props.items.length === 0) return '';
    // The block's id was stamped into props as `__blockId` by render.ts
    // (the same magic prop link-rotator used). Needed so we can read
    // ctx.rotatorPicks for each icon's URL pool.
    const blockId =
      (props as unknown as { __blockId?: string }).__blockId || '';

    const icons = props.items
      .map((it, itemIndex) => {
        // Per-icon URL selection. Order of preference for the index:
        //   1. ctx.rotatorPicks[`${blockId}:${itemIndex}`] — bouncer's
        //      click-weighted pick.
        //   2. Math.random() — uniform fallback (editor preview + cold
        //      cache). Single-URL items skip this entirely; their lone
        //      URL stays at index 0.
        let chosenIndex = 0;
        if (it.urls.length > 1) {
          const key = `${blockId}:${itemIndex}`;
          if (
            blockId &&
            ctx?.rotatorPicks &&
            typeof ctx.rotatorPicks[key] === 'number'
          ) {
            const pick = ctx.rotatorPicks[key];
            if (pick >= 0 && pick < it.urls.length) chosenIndex = pick;
          } else {
            chosenIndex = Math.floor(Math.random() * it.urls.length);
          }
        }
        const chosenUrl = it.urls[chosenIndex]!;

        // mailto: handled differently to email URL
        const href =
          it.platform === 'email' && !chosenUrl.startsWith('mailto:')
            ? `mailto:${chosenUrl.trim()}`
            : maybeTokenize(safeUrl(ensureHttps(chosenUrl)), ctx?.tokenizeUrl);
        const target =
          it.platform === 'email' ? '' : 'target="_blank" rel="noopener noreferrer"';
        // data-pb-item-index + data-pb-url-index let the click tracker
        // beacon report which icon was tapped AND which URL inside that
        // icon's pool was served. The bouncer uses both to keep the
        // rebalance cache fresh per (block, item) pool.
        const trackingAttrs =
          it.urls.length > 1
            ? `data-pb-item-index="${itemIndex}" data-pb-url-index="${chosenIndex}"`
            : '';
        return `<a href="${href}" ${target} ${trackingAttrs} style="display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;color:var(--text);opacity:0.7;transition:opacity 0.15s;" onmouseover="this.style.opacity=1" onmouseout="this.style.opacity=0.7" aria-label="${it.platform}"><span style="width:24px;height:24px;display:inline-block;">${ICONS[it.platform]}</span></a>`;
      })
      .join('');
    return `<div class="pb-block pb-social-row" style="display:flex;gap:8px;justify-content:${
      props.align === 'left' ? 'flex-start' : props.align === 'right' ? 'flex-end' : 'center'
    };flex-wrap:wrap;">${icons}</div>`;
  },
};
